import { useEffect, useState } from 'react';
import { ArrowsClockwise, Plus, DownloadSimple } from '@phosphor-icons/react';
import { Modal, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment';
import { AppTable, TablePagination } from '../../components';
import { HoverBorderButton, StyledButton, ColoredPill, MuteText } from '../../elements';
import { InventoryService, ToastMessage, theme, CommonUtility } from '../../utility';
import type { ITableColumn } from '../../types/common/table';

interface InventoryExportFeedsProps {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  filters: any;
}

interface FeedResult {
  processed: number;
  successful: number;
  failed: number;
  errors: any[];
}

interface Feed {
  _id: string;
  createdAt: string;
  status: string;
  s3Key?: string;
  s3Bucket?: string;
  url?: string;
  results?: FeedResult;
}

interface FeedRowProps {
  item?: Feed;
  index?: number;
}

function FeedRow({ item }: FeedRowProps) {
  if (!item) return null;

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Table.Row>
      <Table.Cell>
        {item.createdAt ? (
          <>
            {moment(item.createdAt).format('MMM DD, YYYY')}
            <br />
            <MuteText>{moment(item.createdAt).format('hh:mm A')}</MuteText>
          </>
        ) : (
          'N/A'
        )}
      </Table.Cell>
      <Table.Cell>
        <ColoredPill
          color={
            item.status.toLowerCase() === 'completed'
              ? theme.colors.green
              : item.status.toLowerCase() === 'failed'
                ? theme.colors.red
                : item.status.toLowerCase() === 'processing'
                  ? theme.colors.yellow
                  : theme.colors.orange
          }
        >
          &#9679; {CommonUtility.toTitleCase(item.status) || 'N/A'}
        </ColoredPill>
      </Table.Cell>
      <Table.Cell>
        {item.status === 'completed' && item.url ? (
          <HoverBorderButton
            onClick={() => handleDownload(item.url!, item.s3Key || 'export.csv')}
            className="flex-btn"
          >
            <DownloadSimple className="mr-1" /> Download
          </HoverBorderButton>
        ) : null}
      </Table.Cell>
    </Table.Row>
  );
}

export function InventoryExportFeeds({
  open,
  onClose,
  warehouseId,
  filters,
}: InventoryExportFeedsProps) {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchFeeds = async () => {
    try {
      setLoading(true);
      const params = {
        warehouseId,
        page,
        limit,
      };
      const res = await InventoryService.getInventoryExportFeeds(params);
      setFeeds(res.data.feeds);
      setTotalData(res.totalCount);
      setTotalPages(res.totalPages);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && warehouseId) {
      fetchFeeds();
    }
  }, [open, warehouseId, page, limit]);

  const handleCreateFeed = async () => {
    try {
      setCreateLoading(true);
      const params = {
        warehouseId,
        ...filters,
      };
      delete params.page;
      delete params.limit;

      await InventoryService.createInventoryExportFeed(params);
      ToastMessage.success('Export feed created successfully');
      fetchFeeds();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const columns: ITableColumn[] = [
    {
      text: 'Created At',
      key: 'createdAt',
      width: 5,
      textAlign: 'left',
    },
    {
      text: 'Status',
      key: 'status',
      width: 4,
      textAlign: 'left',
    },
    {
      text: 'Download',
      key: 'download',
      width: 4,
      textAlign: 'left',
    },
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <Header>
        <h2 className="m-0">Inventory Export Feeds</h2>
        <ActionsWrapper>
          <StyledButton
            onClick={handleCreateFeed}
            loading={createLoading}
            className="flex-btn mr-2"
          >
            <Plus size={18} className="mr-2" />
            New Export
          </StyledButton>
          <HoverBorderButton
            onClick={fetchFeeds}
            disabled={loading}
            style={{
              width: '37px',
              height: '37px',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ArrowsClockwise size={16} />
          </HoverBorderButton>
        </ActionsWrapper>
      </Header>

      <TableWrapper>
        <AppTable
          columns={columns}
          data={feeds}
          loading={loading}
          totalData={totalData}
          totalPages={totalPages}
          RowComponent={<FeedRow />}
          noShadow
          wrapperClass="mt-0"
        />
      </TableWrapper>

      <div className="mx-4">
        <TablePagination
          noPadding
          colSpan={columns.length}
          currentPage={page}
          totalPages={totalPages}
          pageChanged={setPage}
          pageSizeChanged={setLimit}
          pageSize={limit}
          totalData={totalData}
        />
      </div>

      <StickyFooter>
        <HoverBorderButton onClick={onClose} style={{ width: '100px' }}>
          Close
        </HoverBorderButton>
      </StickyFooter>
    </Modal>
  );
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 20px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TableWrapper = styled.div`
  padding: 0 20px;
  margin-bottom: 0px;
  max-height: 50vh;
  overflow-y: auto;
  margin-top: 10px;
`;

const StickyFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 10;
`;
